import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, Phone, Mail, MapPin } from "lucide-react";
import { PHONE_NUMBER, PHONE_TEL, PRMG_ADDRESS, APPLY_URL } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_OPTIONS = [
  "Morning (8am-12pm)",
  "Afternoon (12pm-5pm)",
  "Evening (5pm-8pm)",
  "Anytime",
];

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    best_time: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e?.target?.value ?? e }));

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill out your name, email, and phone.");
      return;
    }
    if (!isValidEmail(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/callback`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        best_time: form.best_time || null,
        message: form.message.trim() || null,
      });
      toast.success("Thanks! Randy will reach out shortly.", {
        description: "Your callback request has been received.",
      });
      setForm({ name: "", email: "", phone: "", best_time: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Couldn't submit your request.", {
        description: "Please try again or call us directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-28 lg:py-32 bg-[#F8F9FA] border-t border-[#E2E8F0]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16">
        {/* Info */}
        <div className="lg:col-span-5">
          <p className="text-xs font-semibold tracking-[0.22em] uppercase text-[#F26A21] mb-3">
            Let&rsquo;s discuss your options
          </p>
          <h2
            data-testid="contact-heading"
            className="font-display font-bold text-[#0A2F6B] text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight"
          >
            Schedule a no-obligation consultation today.
          </h2>
          <p className="mt-5 text-base md:text-lg text-[#475569] leading-relaxed">
            A HELOC could be the right solution for your needs. Fill out the form and
            Randy will reach out at the time that works best for you.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href={PHONE_TEL}
              data-testid="contact-phone-link"
              className="flex items-start gap-4 group"
            >
              <div className="w-11 h-11 rounded-md bg-[#0A2F6B] flex items-center justify-center shrink-0 group-hover:bg-[#F26A21] transition-colors">
                <Phone size={18} className="text-white" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#475569] font-semibold">Call directly</p>
                <p className="font-display font-semibold text-[#0A2F6B] text-lg tabular-nums">{PHONE_NUMBER}</p>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-md bg-[#0A2F6B] flex items-center justify-center shrink-0">
                <Mail size={18} className="text-white" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#475569] font-semibold">Apply online</p>
                <a
                  href={APPLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-apply-link"
                  className="font-display font-semibold text-[#0A2F6B] text-base hover:text-[#F26A21] transition-colors break-all"
                >
                  Start your secure application
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-md bg-[#0A2F6B] flex items-center justify-center shrink-0">
                <MapPin size={18} className="text-white" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#475569] font-semibold">PRMG Headquarters</p>
                <p className="text-[#0F172A] text-sm leading-relaxed max-w-xs">{PRMG_ADDRESS}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-7">
          <form
            data-testid="callback-form"
            onSubmit={onSubmit}
            className="bg-white border-2 border-[#0A2F6B]/15 rounded-lg p-6 md:p-10 shadow-md ring-1 ring-[#0A2F6B]/5"
            noValidate
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name" className="text-[#0A2F6B] font-semibold text-sm">Full Name</Label>
                <Input
                  id="name"
                  data-testid="callback-name-input"
                  placeholder="Jane Homeowner"
                  value={form.name}
                  onChange={update("name")}
                  className="mt-2 h-11 border-[#E2E8F0] focus-visible:ring-[#0A2F6B] focus-visible:ring-2"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-[#0A2F6B] font-semibold text-sm">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  data-testid="callback-phone-input"
                  placeholder="(555) 123-4567"
                  value={form.phone}
                  onChange={update("phone")}
                  className="mt-2 h-11 border-[#E2E8F0] focus-visible:ring-[#0A2F6B] focus-visible:ring-2"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
              <div>
                <Label htmlFor="email" className="text-[#0A2F6B] font-semibold text-sm">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="callback-email-input"
                  placeholder="jane@email.com"
                  value={form.email}
                  onChange={update("email")}
                  className="mt-2 h-11 border-[#E2E8F0] focus-visible:ring-[#0A2F6B] focus-visible:ring-2"
                  required
                />
              </div>
              <div>
                <Label htmlFor="time" className="text-[#0A2F6B] font-semibold text-sm">Best time to call</Label>
                <Select value={form.best_time} onValueChange={update("best_time")}>
                  <SelectTrigger
                    id="time"
                    data-testid="callback-time-select"
                    className="mt-2 h-11 border-[#E2E8F0] focus:ring-[#0A2F6B] focus:ring-2"
                  >
                    <SelectValue placeholder="Choose a time" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt} data-testid={`time-option-${opt.split(" ")[0].toLowerCase()}`}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-5">
              <Label htmlFor="message" className="text-[#0A2F6B] font-semibold text-sm">
                How can we help? <span className="text-[#94A3B8] font-normal">(optional)</span>
              </Label>
              <Textarea
                id="message"
                data-testid="callback-message-input"
                placeholder="Tell us a bit about what you're looking to accomplish..."
                value={form.message}
                onChange={update("message")}
                className="mt-2 min-h-[120px] border-[#E2E8F0] focus-visible:ring-[#0A2F6B] focus-visible:ring-2"
              />
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <p className="text-xs text-[#94A3B8] max-w-md leading-relaxed">
                By submitting, you consent to be contacted by Randy Wilson regarding HELOC options.
                We never share your information.
              </p>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="callback-submit-button"
                className="bg-[#F26A21] hover:bg-[#D95A1A] text-white font-semibold px-6 py-6 h-12 rounded-md shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                {submitting ? "Sending..." : "Request callback"}
                <ArrowRight size={18} strokeWidth={2.5} className="ml-2" />
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
